import { useContext, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"

import { cn } from "../../lib/utils"
import { PortalContainerContext } from "./portal-container"

type TooltipProps = {
  content: ReactNode
  children: ReactNode
  className?: string
  /** Which side of the trigger the tooltip opens on. */
  placement?: "top" | "bottom" | "right"
}

const TOOLTIP_MAX_WIDTH = 320

/** Lightweight hover/focus tooltip. Renders into a body portal with fixed
 *  positioning so it escapes scroll-container clipping (e.g. the feed table's
 *  overflow), unlike a CSS-only absolute tooltip. Single trigger, no arrow; just
 *  a viewport-edge clamp rather than full collision handling. */
export function Tooltip({ content, children, className, placement = "bottom" }: TooltipProps) {
  const portalContainer = useContext(PortalContainerContext)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const above = placement === "top"
  const beside = placement === "right"

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) {
      return
    }
    const desiredLeft = beside ? rect.right + 6 : rect.left
    const left = Math.max(8, Math.min(desiredLeft, window.innerWidth - TOOLTIP_MAX_WIDTH - 8))
    // For "top" we anchor at the trigger's top edge and shift the box up by its
    // own height via a CSS transform (the height isn't known until render).
    setCoords({ top: tooltipTop(rect, placement), left })
  }
  const close = () => setCoords(null)

  return (
    <span
      ref={triggerRef}
      className={cn("inline-flex", className)}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {children}

      {coords
        ? createPortal(
            <div
              role="tooltip"
              style={{ top: coords.top, left: coords.left, maxWidth: TOOLTIP_MAX_WIDTH }}
              className={cn(
                "pointer-events-none fixed z-[80] rounded-lg border border-border bg-popover px-3 py-2 text-xs leading-relaxed text-popover-foreground shadow-floating animate-in fade-in-0 zoom-in-95 duration-100",
                above && "-translate-y-full",
                beside && "-translate-y-1/2"
              )}
            >
              {content}
            </div>,
            portalContainer ?? document.body
          )
        : null}
    </span>
  )
}

function tooltipTop(rect: DOMRect, placement: NonNullable<TooltipProps["placement"]>): number {
  if (placement === "top") {
    return rect.top - 6
  }
  if (placement === "right") {
    return rect.top + rect.height / 2
  }
  return rect.bottom + 6
}
